import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import TitleCard from "../cards/TitleCard";
import MainCard from "../cards/MainCard";
import Icon from "../icons/Icon";
import WellBeingFoundationsSection from "../sections/WellbeingFoundations";
import styles from "./UserDashboardContent.module.scss";
import ImageGettingstarted from "../assets/gettingstartedcard.svg?url";
import ImageResources from "../assets/educatorcard.svg?url";
import ImageSupport from "../assets/supportcard.svg?url";
import ImageAccount from "../assets/accountcard.svg?url";
import ImageViewActiveLessons from "../assets/view-active-lessons.png";
import ImagePlatformTour from "../assets/platform-tour.png";
import ImageGettingStartedGuide from "../assets/getting-started-guide.png";
import ImageLessonRoadMaps from "../assets/lesson-roadmaps.png";
import ImageAuCareResources from "../assets/au-caresources.png";
import ImageResilient from "../assets/imagecrc.png";
import ImageQuickStart from "../assets/quickstart-for-educators.png";
import {
  IconGettingStarted,
  IconSupport,
  IconEducatorOnboarding,
  IconAdultResource,
  IconProfessional,
  IconCaregiver,
  IconHelp,
  IconChat,
  IconLessonProgress,
  IconMyAccount,
} from "@src/constants";
import DashboardCard from "../cards/DashboardCard";
import ImageWellbeingFoundations from "../assets/card-icons/wellbeing-foundations.svg?url";
import ImagePrograms from "../assets/card-icons/programs.svg?url";
import ImageStandaloneLessons from "../assets/card-icons/standalone-lessons.svg?url";
import ImageEducatorOnboarding from "../assets/card-icons/educator-onboarding.svg?url";
import ImageLessonRoadmaps from "../assets/card-icons/lesson-roadmaps.svg?url";
import ImageQuickstartArticles from "../assets/card-icons/quickstart-articles.svg?url";

type UserDashboardContentProps = {
  isAussie: boolean | "" | undefined;
  isHasLessonLibrary: boolean;
  isUS: boolean | "" | undefined;
  openPlatformTour: () => void;
  openQuickStartEducator: () => void;
  intercomGettingStarted: () => void;
  openProfessionalDevelopment: () => void;
  openCaregiver: () => void;
  openHelpCentre: () => void;
  openLiveChat: () => void;
  openLessonProgress: () => void;
  openMyProfile: () => void;
  classroomLessons: () => React.ReactNode;
  reports: () => React.ReactNode;
};

type CardItemProps = {
  title: string;
  description: string;
  icon?: string;
  onClick?: () => void;
  backgroundColor?: "Orange" | "Blue" | "Green" | "Purple";
} [];

function UserDashboardContent({
  isAussie,
  isHasLessonLibrary = false,
  isUS,
  openPlatformTour,
  openQuickStartEducator,
  intercomGettingStarted,
  openProfessionalDevelopment,
  openCaregiver,
  openHelpCentre,
  openLiveChat,
  openLessonProgress,
  openMyProfile,
  classroomLessons,
  reports,
}: UserDashboardContentProps) {

  const history = useHistory();

  const openWellbeingFoundationsCard = () => {
    history.replace(`/wellbeingfoundations?tab=student-introduction`)
  }

  const openPrograms = useCallback(() => {
    history.push("/lessons?tab=programs");
  }, [history]);

  const openStandaloneLessonsCard = useCallback(() => {
    history.replace("/lessons?tab=library");
  }, [history]);

  const openLessonLibraryCard = useCallback(() => {
    history.replace("/lessons?tab=library");
  }, [history]);

  const openEducatorOnboardingCard = useCallback(() => {
    if (isAussie) {
      window.open(
        "https://learn.openparachuteschools.com/program/educatoronboarding?pid=5aDstSYmlT4S72kqh5h1O",
        "_blank"
      );
      return;
    } else {
      window.open(
        "https://learn.openparachuteschools.com/program/educatoronboarding?pid=aLYHriE6Nx6geYDZ_gTij",
        "_blank"
      );
      return;
    }
  }, [isAussie]);

  const openLessonRoadmapsCard = useCallback(() => {
    if (isAussie) {
      window.open(
        "https://help.openparachuteschools.com/en/collections/10703611-roadmaps",
        "_blank"
      );
      return;
    } else {
      window.open(
        "https://help.openparachuteschools.com/en/collections/10703611-roadmaps",
        "_blank"
      );
      return;
    }
  }, [isAussie]);

  const openQuickStartEducatorCard = useCallback(() => {
    if (isAussie) {
      window.open(
        "https://help.openparachuteschools.com/en/collections/10400596-quickstart-for-educators",
        "_blank"
      );
      return;
    } else {
      window.open(
        "https://help.openparachuteschools.com/en/collections/10400596-quickstart-for-educators",
        "_blank"
      );
      return;
    }
  }, [isAussie]);

  const itemsClassroomLessons: CardItemProps = useMemo(() => {
    if (isAussie) {
      return [
        {
          title: "Wellbeing\n Foundations",
          description: "Student introduction lessons, brain break activities and skill-building exercises",
          icon: ImageWellbeingFoundations,
          onClick: openWellbeingFoundationsCard,
          backgroundColor: "Orange",
        },
        {
          title: "Programs",
          description: "Collections of lessons to allow deeper learning into a specific topic or area",
          icon: ImagePrograms,
          onClick: openPrograms,
          backgroundColor: "Orange",
        },
        ...(isHasLessonLibrary ? [{
          title: "Standalone\n Lessons",
          description: "View the main hub for all available standalone lessons in Years 7 through 12",
          icon: ImageStandaloneLessons,
          onClick: openStandaloneLessonsCard,
          backgroundColor: "Orange" as const,
        }] : []),
      ]
    } else {
      return [
        {
          title: "Well-being\n Foundations",
          description: "Student introduction lessons, brain break activities and skill-building exercises",
          icon: ImageWellbeingFoundations,
          onClick: openWellbeingFoundationsCard,
          backgroundColor: "Orange",
        },
        {
          title: "Programs",
          description: "Collections of lessons to allow deeper learning into a specific topic or area",
          icon: ImagePrograms,
          onClick: openPrograms,
          backgroundColor: "Orange",
        },
        ...(isHasLessonLibrary ? [{
          title: "Lesson\n Library",
          description: "View the main hub for our full catalog of standalone lessons",
          icon: ImageStandaloneLessons,
          onClick: openLessonLibraryCard,
          backgroundColor: "Orange" as const,
        }] : []),
      ]
    }
  }, [isAussie, isHasLessonLibrary]);

  const itemsNotSureWhereToStart: CardItemProps = useMemo(() => {
    if (isAussie) {
      return [
        {
          title: "Educator\n Onboarding",
          description: "Prepare educators to confidently deliver the program in classrooms",
          icon: ImageEducatorOnboarding,
          onClick: openEducatorOnboardingCard,
          backgroundColor: "Blue",
        },
        {
          title: "Lesson\n Roadmaps",
          description: "Suggested lesson planning for each Year level to guide you through the program",
          icon: ImageLessonRoadmaps,
          onClick: openLessonRoadmapsCard,
          backgroundColor: "Blue",
        },
        {
          title: "Quickstart\n Articles",
          description: "View informative Help Center guides for using the platform and running lessons",
          icon: ImageQuickstartArticles,
          onClick: openQuickStartEducatorCard,
          backgroundColor: "Blue",
        }
      ]
    } else {
      return [
        {
          title: "Educator\n Onboarding",
          description: "Prepare educators to confidently deliver the program in classrooms",
          icon: ImageEducatorOnboarding,
          onClick: openEducatorOnboardingCard,
          backgroundColor: "Blue",
        },
        {
          title: "Lesson\n Roadmaps",
          description: "Lesson recommendations for every grade level to get started quickly",
          icon: ImageLessonRoadmaps,
          onClick: openLessonRoadmapsCard,
          backgroundColor: "Blue",
        },
        {
          title: "Quickstart\n Articles",
          description: "View informative Help Center guides for using the platform and running lessons",
          icon: ImageQuickstartArticles,
          onClick: openQuickStartEducatorCard,
          backgroundColor: "Blue",
        }
      ]
    }
  }, [isAussie]);
  
  return (
    <div className={styles.mainView}>
      <div className={styles.panelContainer}>
        <h2 className={styles.panelTitle}>Classroom Lessons</h2>
        <div className={styles.cardGroup}>
          {itemsClassroomLessons.map((item, index) => (
            <DashboardCard
              key={index}
              title={item.title}
              description={item.description}
              icon={item.icon}
              onClick={item.onClick}
              backgroundColor={item.backgroundColor}
            />
          ))}
        </div>
      </div>

      <div className={styles.panelContainer}>
        <h2 className={styles.panelTitle}>Not Sure Where To Start?</h2>
        <div className={styles.cardGroup}>
          {itemsNotSureWhereToStart.map((item, index) => (
            <DashboardCard
              key={index}
              title={item.title}
              description={item.description}
              icon={item.icon}
              onClick={item.onClick}
              backgroundColor={item.backgroundColor}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default UserDashboardContent;
